import { useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { route, back } from '@/state/nav';
import { db } from '@/lib/db';
import { speakText, stopSpeaking, speaking } from '@/lib/tts';
import { showToast } from '@/state/ui';
import { Icon } from '@/components/Icon';
import type { Report } from '@/types';

/** Tiny markdown renderer scoped to what the agent writes:
 *  #/##/### headers -> section heads, - lists, **bold**, plain paragraphs. */
function renderMd(md: string): JSX.Element[] {
  const out: JSX.Element[] = [];
  let list: string[] = [];
  let key = 0;
  const bold = (s: string) => {
    const parts = s.split(/\*\*(.+?)\*\*/g);
    return parts.map((p, i) => (i % 2 ? <b key={i}>{p}</b> : p));
  };
  const flushList = () => {
    if (!list.length) return;
    out.push(<ul key={key++} style={{ margin: '0 0 4px', padding: 0 }}>{list.map((l, i) => <li key={i}>{bold(l)}</li>)}</ul>);
    list = [];
  };
  for (const raw of md.split('\n')) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushList();
      continue;
    }
    const h = line.match(/^#{1,4}\s+(.*)/);
    if (h) {
      flushList();
      out.push(<p key={key++} class="sec-h">{h[1]}</p>);
      continue;
    }
    const li = line.match(/^[-*•]\s+(.*)/);
    if (li) {
      list.push(li[1]);
      continue;
    }
    flushList();
    out.push(<p key={key++}>{bold(line)}</p>);
  }
  flushList();
  return out;
}

/** Strip markdown for clipboard/speech. */
function plain(md: string): string {
  return md
    .replace(/^#{1,4}\s+/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/^[-*•]\s+/gm, '— ');
}

export function ReportOutputScreen() {
  const id = route.value.params.id;
  const [report, setReport] = useState<Report | null>(null);

  useEffect(() => {
    void db.reports.get(id).then((r) => setReport(r ?? null));
    return () => stopSpeaking();
  }, [id]);

  if (!report) {
    return (
      <div class="screen screen--no-tabbar">
        <header class="header header--sub">
          <button class="iconbtn" aria-label="Back" onClick={() => back('/reports')}>
            <Icon name="chevron-left" size={20} />
          </button>
        </header>
        <p class="roster-empty">Report not found.</p>
      </div>
    );
  }

  const copy = async () => {
    await navigator.clipboard.writeText(`${report.title}\n\n${plain(report.content_md)}`);
    showToast({ kind: 'success', message: 'Copied' });
  };

  const readAloud = () => {
    if (speaking.value) stopSpeaking();
    else speakText(plain(report.content_md));
  };

  const share = async () => {
    const text = `${report.title}\n\n${plain(report.content_md)}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: report.title, text });
      } catch { /* canceled */ }
    } else {
      await navigator.clipboard.writeText(text);
      showToast({ kind: 'info', message: 'Sharing not available here — copied instead' });
    }
  };

  const when = new Date(report.created_at);

  return (
    <div class="screen screen--no-tabbar screen--fade-in">
      <header class="header header--sub">
        <button class="iconbtn" aria-label="Back" onClick={() => back('/reports')}>
          <Icon name="chevron-left" size={20} />
        </button>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <p class="t-meta" style={{ margin: 0 }}>
            Generated {when.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase()}
          </p>
          <div style={{ fontWeight: 600, fontSize: '13px' }}>{report.rtype === 'handoff' ? 'Shift handoff' : report.title}</div>
        </div>
        <span style={{ width: '40px' }} />
      </header>

      <div class="scroll" style={{ paddingBottom: '110px' }}>
        <div class="paper">
          <p class="r-h">{report.title}</p>
          <p class="r-meta">
            {when.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
          {renderMd(report.content_md)}
          <p class="r-end">— end of report —</p>
        </div>
      </div>

      <div class="actbar">
        <button class="act" onClick={copy}>
          <Icon name="copy" />
          Copy
        </button>
        <button class="act act--p" onClick={readAloud}>
          <Icon name={speaking.value ? 'speaker-off' : 'speaker'} />
          {speaking.value ? 'Stop' : 'Read aloud'}
        </button>
        <button class="act" onClick={share}>
          <Icon name="share" />
          Share
        </button>
      </div>
    </div>
  );
}
